import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, ColorPicker, DatePicker, Form, Input, InputNumber, Layout, message, Modal, Popconfirm, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import apis from '../../apis';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';

const { Content } = Layout;
const { Title, Text } = Typography;

const AWARD_TIERS = ['Sapphire', 'Diamond', 'Gold', 'Silver', 'Public'];

const TicketPricingSettings = () => {
    const { currentEventId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);

    // Data States
    const [ticketTiers, setTicketTiers] = useState([]);
    const [addOns, setAddOns] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [isEligibilityEnabled, setIsEligibilityEnabled] = useState(false);

    // Modal States
    const [isTierModalVisible, setIsTierModalVisible] = useState(false);
    const [isAddOnModalVisible, setIsAddOnModalVisible] = useState(false);
    const [editingTier, setEditingTier] = useState(null);
    const [editingAddOn, setEditingAddOn] = useState(null);
    const [editingEligibility, setEditingEligibility] = useState(null);
    const [isEligibilityModalVisible, setIsEligibilityModalVisible] = useState(false);

    const [tierForm] = Form.useForm();
    const [addOnForm] = Form.useForm();
    const [eligibilityForm] = Form.useForm();

    const fetchPricingData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setTicketTiers(data.ticketTiers || []);
                setAddOns(data.addOns || []);
            }

            // Fetch eligibility from global settings
            const res = await apis.systemSettings.getGlobalSettings();
            if (res.data?.ticketEligibility) {
                const { enabled, schedule: s } = res.data.ticketEligibility;
                setIsEligibilityEnabled(enabled || false);
                setSchedule(s || []);
            }
        } catch (error) {
            console.error("Error fetching pricing data:", error);
            message.error("Failed to fetch pricing data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentEventId) {
            fetchPricingData();
        }
    }, [currentEventId]);

    // --- Ticket Tiers Logic ---
    const handleAddTier = () => {
        setEditingTier(null);
        tierForm.resetFields();
        tierForm.setFieldsValue({ color: '#1677ff' }); // default color
        setIsTierModalVisible(true);
    };

    const handleEditTier = (record) => {
        setEditingTier(record);
        tierForm.setFieldsValue({
            ...record,
            color: record.color || '#1677ff'
        });
        setIsTierModalVisible(true);
    };

    const handleDeleteTier = async (id) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this ticket tier?',
            content: 'This might break seat map mappings if this tier is still assigned to rows in Venue Settings.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                const updatedTiers = ticketTiers.filter(t => t.id !== id);
                await saveTiers(updatedTiers);
            }
        });
    };

    const saveTiers = async (updatedTiers) => {
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            await updateDoc(docRef, { ticketTiers: updatedTiers });
            setTicketTiers(updatedTiers);
            message.success("Ticket tiers updated successfully");
        } catch (error) {
            console.error("Error updating tiers:", error);
            message.error("Failed to update ticket tiers");
        }
    };

    const onTierModalOk = () => {
        tierForm.validateFields().then(async (values) => {
            const newTier = {
                id: values.id.trim(),
                name: values.name.trim(),
                price: values.price,
                color: typeof values.color === 'string' ? values.color : values.color.toHexString()
            };

            let updatedTiers;
            if (editingTier) {
                updatedTiers = ticketTiers.map(t => t.id === editingTier.id ? { ...newTier, id: editingTier.id } : t);
            } else {
                if (ticketTiers.some(t => t.id === newTier.id)) {
                    message.error("A tier with this ID already exists.");
                    return;
                }
                updatedTiers = [...ticketTiers, newTier];
            }

            await saveTiers(updatedTiers);
            setIsTierModalVisible(false);
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };

    // --- Add-Ons Logic ---
    const handleAddAddOn = () => {
        setEditingAddOn(null);
        addOnForm.resetFields();
        setIsAddOnModalVisible(true);
    };

    const handleEditAddOn = (record) => {
        setEditingAddOn(record);
        addOnForm.setFieldsValue(record);
        setIsAddOnModalVisible(true);
    };

    const handleDeleteAddOn = async (id) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this add-on?',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                const updatedAddOns = addOns.filter(a => a.id !== id);
                await saveAddOns(updatedAddOns);
            }
        });
    };

    const saveAddOns = async (updatedAddOns) => {
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            await updateDoc(docRef, { addOns: updatedAddOns });
            setAddOns(updatedAddOns);
            message.success("Add-Ons updated successfully");
        } catch (error) {
            console.error("Error updating add-ons:", error);
            message.error("Failed to update add-ons");
        }
    };

    const onAddOnModalOk = () => {
        addOnForm.validateFields().then(async (values) => {
            const newAddOn = {
                id: values.id.trim(),
                name: values.name.trim(),
                price: values.price,
                description: values.description || ''
            };

            let updatedAddOns;
            if (editingAddOn) {
                updatedAddOns = addOns.map(a => a.id === editingAddOn.id ? { ...newAddOn, id: editingAddOn.id } : a);
            } else {
                if (addOns.some(a => a.id === newAddOn.id)) {
                    message.error("An add-on with this ID already exists.");
                    return;
                }
                updatedAddOns = [...addOns, newAddOn];
            }

            await saveAddOns(updatedAddOns);
            setIsAddOnModalVisible(false);
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };

    // --- Eligibility Logic ---
    const handleSaveEligibility = async (newIsEnabled = isEligibilityEnabled, newSchedule = schedule) => {
        setSaveLoading(true);
        try {
            const payload = {
                ticketEligibility: {
                    enabled: newIsEnabled,
                    schedule: newSchedule
                }
            };
            const res = await apis.systemSettings.updateGlobalSettings(payload);
            if (res.error) throw new Error(res.error);

            setIsEligibilityEnabled(newIsEnabled);
            setSchedule(newSchedule);
            message.success('Ticket eligibility settings updated');
        } catch (error) {
            console.error("Error updating eligibility:", error);
            message.error("Failed to update ticket eligibility settings.");
        } finally {
            setSaveLoading(false);
        }
    };

    const addScheduleEntry = (values) => {
        const dateStr = values.date.format('YYYY-MM-DD');
        if (schedule.some(item => item.date === dateStr)) {
            message.error('An entry for this date already exists');
            return;
        }
        const newSchedule = [...schedule, { date: dateStr, allowedTiers: values.tiers }];
        newSchedule.sort((a, b) => a.date.localeCompare(b.date));
        handleSaveEligibility(isEligibilityEnabled, newSchedule);
        eligibilityForm.resetFields();
    };

    const removeScheduleEntry = (date) => {
        const newSchedule = schedule.filter(item => item.date !== date);
        handleSaveEligibility(isEligibilityEnabled, newSchedule);
    };

    const handleEditEligibility = (record) => {
        setEditingEligibility(record);
        eligibilityForm.setFieldsValue({
            date: dayjs(record.date),
            tiers: record.allowedTiers
        });
        setIsEligibilityModalVisible(true);
    };

    const onEligibilityModalOk = () => {
        eligibilityForm.validateFields().then(async (values) => {
            const dateStr = values.date.format('YYYY-MM-DD');
            const newEntry = { date: dateStr, allowedTiers: values.tiers };

            let updatedSchedule;
            if (editingEligibility) {
                // If editing, replace the entry at the original date
                updatedSchedule = schedule.map(item => item.date === editingEligibility.date ? newEntry : item);
            } else {
                // This shouldn't happen with the current UI flow but good to have
                if (schedule.some(item => item.date === dateStr)) {
                    message.error('An entry for this date already exists');
                    return;
                }
                updatedSchedule = [...schedule, newEntry];
            }

            updatedSchedule.sort((a, b) => a.date.localeCompare(b.date));
            await handleSaveEligibility(isEligibilityEnabled, updatedSchedule);
            setIsEligibilityModalVisible(false);
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };

    // --- Table Columns ---
    const tierColumns = [
        {
            title: 'Tier ID',
            dataIndex: 'id',
            key: 'id',
            render: text => <Text strong>{text}</Text>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Price (IDR)',
            dataIndex: 'price',
            key: 'price',
            render: price => `Rp ${price.toLocaleString('id-ID')}`,
        },
        {
            title: 'Map Color',
            dataIndex: 'color',
            key: 'color',
            render: color => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: 24, height: 24, backgroundColor: color || '#888', borderRadius: 4, border: '1px solid #d9d9d9' }} />
                    <Text type="secondary">{color || 'Not set'}</Text>
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEditTier(record)} />
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteTier(record.id)} />
                </Space>
            ),
        },
    ];

    const addOnColumns = [
        {
            title: 'Add-On ID',
            dataIndex: 'id',
            key: 'id',
            render: text => <Text strong>{text}</Text>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Price (IDR)',
            dataIndex: 'price',
            key: 'price',
            render: price => `Rp ${price.toLocaleString('id-ID')}`,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEditAddOn(record)} />
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAddOn(record.id)} />
                </Space>
            ),
        },
    ];

    const eligibilityColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => dayjs(text).format('DD MMM YYYY'),
            sorter: (a, b) => a.date.localeCompare(b.date),
        },
        {
            title: 'Allowed Tiers',
            dataIndex: 'allowedTiers',
            key: 'allowedTiers',
            render: (tiers) => (
                <>
                    {(tiers || []).map(tier => {
                        let color = 'default';
                        if (tier === 'Sapphire') color = 'purple';
                        if (tier === 'Diamond') color = 'cyan';
                        if (tier === 'Gold') color = 'gold';
                        if (tier === 'Silver') color = 'geekblue';
                        if (tier === 'Public') color = 'green';
                        return <Tag color={color} key={tier}>{tier}</Tag>;
                    })}
                </>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEditEligibility(record)} />
                    <Popconfirm title="Remove this entry?" onConfirm={() => removeScheduleEntry(record.date)}>
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const tabItems = [
        {
            key: '1',
            label: 'Ticket Tiers',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary">Define ticket tiers (e.g., Presto, Allegro) and their pricing. These IDs must match the `areaType` configured in Venue Settings.</Text>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTier}>Add Ticket Tier</Button>
                    </div>
                    <Table
                        dataSource={ticketTiers}
                        columns={tierColumns}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                    />
                </div>
            )
        },
        {
            key: '2',
            label: 'Optional Add-Ons',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary">Define unlimited optional extras like physical tickets, merchandise, or parking.</Text>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAddOn}>Add Extra Item</Button>
                    </div>
                    <Table
                        dataSource={addOns}
                        columns={addOnColumns}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                    />
                </div>
            )
        },
        {
            key: '3',
            label: 'Eligibility Schedule',
            children: (
                <Card
                    title="Ticket Eligibility Schedule"
                    bordered={false}
                    extra={
                        <Space>
                            <Text>Enabled</Text>
                            <Switch checked={isEligibilityEnabled} onChange={(val) => handleSaveEligibility(val, schedule)} loading={saveLoading} />
                        </Space>
                    }
                >
                    <Text type="secondary" style={{ display: 'block', marginBottom: '20px' }}>
                        Define a window for specific award tiers to purchase tickets. If disabled, all winners can buy anytime.
                    </Text>

                    <Form form={eligibilityForm} layout="inline" onFinish={addScheduleEntry} style={{ marginBottom: '24px' }}>
                        <Form.Item name="date" rules={[{ required: true, message: 'Pick date' }]}>
                            <DatePicker placeholder="Select Date" />
                        </Form.Item>
                        <Form.Item name="tiers" rules={[{ required: true, message: 'Select at least one' }]}>
                            <Checkbox.Group options={AWARD_TIERS} />
                        </Form.Item>
                        <Form.Item>
                            <Button type="dashed" htmlType="submit" icon={<PlusOutlined />}>
                                Add Entry
                            </Button>
                        </Form.Item>
                    </Form>

                    <Table
                        dataSource={schedule}
                        columns={eligibilityColumns}
                        rowKey="date"
                        pagination={{ pageSize: 5 }}
                        size="small"
                        loading={loading}
                    />
                </Card>
            )
        }
    ];

    return (
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', borderRadius: 8 }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0 }}>Ticket Settings</Title>
                <Text type="secondary">
                    Manage dynamic ticket prices, optional add-ons, and sales eligibility for the current event:
                    <Text strong style={{ color: '#1677ff', marginLeft: '5px' }}>{currentEventId || 'APCS2026'}</Text>
                </Text>
            </div>

            <Tabs defaultActiveKey="1" items={tabItems} />

            {/* Ticket Tier Modal */}
            <Modal
                title={editingTier ? "Edit Ticket Tier" : "Add Ticket Tier"}
                open={isTierModalVisible}
                onOk={onTierModalOk}
                onCancel={() => setIsTierModalVisible(false)}
                okText="Save"
            >
                <Form form={tierForm} layout="vertical">
                    <Form.Item
                        name="id"
                        label="Tier ID"
                        rules={[{ required: true, message: 'Please enter a unique tier ID' }]}
                        help="Must exactly match the areaType defined in Venue Settings (e.g., 'presto', 'allegro')"
                    >
                        <Input disabled={!!editingTier} placeholder="e.g., presto" />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Display Name"
                        rules={[{ required: true, message: 'Please enter the display name' }]}
                    >
                        <Input placeholder="e.g., Gold Tier (Presto)" />
                    </Form.Item>
                    <Form.Item
                        name="price"
                        label="Price (IDR)"
                        rules={[{ required: true, message: 'Please enter the price' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value?.replace(/\Rp\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="color"
                        label="Seat Map Color"
                        rules={[{ required: true, message: 'Please select a color' }]}
                    >
                        <ColorPicker showText />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add-On Modal */}
            <Modal
                title={editingAddOn ? "Edit Add-On" : "Add Add-On"}
                open={isAddOnModalVisible}
                onOk={onAddOnModalOk}
                onCancel={() => setIsAddOnModalVisible(false)}
                okText="Save"
            >
                <Form form={addOnForm} layout="vertical">
                    <Form.Item
                        name="id"
                        label="Add-On ID"
                        rules={[{ required: true, message: 'Please enter a unique add-on ID' }]}
                    >
                        <Input disabled={!!editingAddOn} placeholder="e.g., physical_ticket" />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Display Name"
                        rules={[{ required: true, message: 'Please enter the display name' }]}
                    >
                        <Input placeholder="e.g., Physical Ticket Delivery" />
                    </Form.Item>
                    <Form.Item
                        name="price"
                        label="Price (IDR)"
                        rules={[{ required: true, message: 'Please enter the price' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value?.replace(/\Rp\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea placeholder="Optional details about this extra item..." rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Eligibility Edit Modal */}
            <Modal
                title="Edit Eligibility Entry"
                open={isEligibilityModalVisible}
                onOk={onEligibilityModalOk}
                onCancel={() => setIsEligibilityModalVisible(false)}
                okText="Save"
                confirmLoading={saveLoading}
            >
                <Form form={eligibilityForm} layout="vertical">
                    <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Pick date' }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="tiers" label="Allowed Tiers" rules={[{ required: true, message: 'Select at least one' }]}>
                        <Checkbox.Group options={AWARD_TIERS} />
                    </Form.Item>
                </Form>
            </Modal>

        </Content>
    );
};

export default TicketPricingSettings;
